import {
  Box2,
  BufferGeometry,
  CanvasTexture,
  Float32BufferAttribute,
  Mesh,
  MeshBasicMaterial,
  SRGBColorSpace,
  Vector2,
} from 'three';

import { isDefined } from '../../utils/variables';

export class WmsTexture {
  private canvas?: HTMLCanvasElement;
  quad: Mesh<BufferGeometry, MeshBasicMaterial> = this.createQuad();

  setCanvas(canvas: HTMLCanvasElement | undefined, extents: Box2) {
    if (!isDefined(canvas)) {
      this.updateTexture(undefined);
    } else if (!isDefined(this.canvas) || !this.canvas.isEqualNode(canvas)) {
      this.updateTexture(canvas);
    } else {
      // Same canvas: refresh texture so we show latest tiles (OpenLayers redraws into the same canvas).
      if (this.quad.material.map) {
        this.quad.material.map.needsUpdate = true;
      }
    }

    this.updateTransform(extents);
  }

  getScene() {
    return this.quad;
  }

  private updateTexture(canvas?: HTMLCanvasElement) {
    this.canvas = canvas;
    this.quad.material.map = canvas ? new CanvasTexture(canvas) : null;
    this.quad.material.opacity = canvas ? 1 : 0;
    this.quad.material.transparent = true;
    this.quad.material.needsUpdate = true;

    if (this.quad.material.map) {
      this.quad.material.map.colorSpace = SRGBColorSpace;
      this.quad.material.map.needsUpdate = true;
    }
  }

  private updateTransform(extents: Box2) {
    const size = extents.getSize(new Vector2());
    const center = extents.getCenter(new Vector2());
    this.quad.position.set(center.x, center.y, -1);
    // Avoid zero or negative scale so the quad is always visible when extents are valid.
    const sx = Math.max(Math.abs(size.x), 1e-6);
    const sy = Math.max(Math.abs(size.y), 1e-6);
    this.quad.scale.set(sx, sy, 1);
    this.quad.updateMatrixWorld(true);
  }

  private createQuad() {
    const material = new MeshBasicMaterial({
      depthWrite: false,
      depthTest: true,
      transparent: false,
    });

    const min = -0.5;
    const max = 0.5;

    // prettier-ignore
    const position = [
      min, min, 0, // Bottom left.
      min, max, 0, // Top left.
      max, min, 0, // Bottom right.
      max, max, 0, // Top right.
    ];

    const normals = [0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1];

    const uvs = [0, 0, 0, 1, 1, 0, 1, 1];

    const indices = [0, 2, 1, 1, 2, 3];

    const geometry = new BufferGeometry();

    geometry.setIndex(indices);
    geometry.setAttribute('position', new Float32BufferAttribute(position, 3));
    geometry.setAttribute('normal', new Float32BufferAttribute(normals, 3));
    geometry.setAttribute('uv', new Float32BufferAttribute(uvs, 2));
    geometry.computeBoundingSphere();

    return new Mesh(geometry, material);
  }
}
